import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  Divider,
  CircularProgress,
  Snackbar,
} from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import { makeStyles } from "@material-ui/core/styles";
import { staffAppointmentService } from "../../services/staff/staffAppointmentService";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  dialogContent: {
    paddingTop: theme.spacing(3),
  },
  settingRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2, 0),
  },
  settingLabel: {
    fontSize: "1rem",
    fontWeight: 500,
  },
  settingDescription: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
    maxWidth: "70%",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
  },
}));

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

function NotificationsSettingsModal({ open, handleClose, clinicId }) {
  const classes = useStyles();

  // Estados para cada tipo de notificación
  const [notificationSettings, setNotificationSettings] = useState({
    recordatorio_cita: false,
    cita_rechazada: false,
    cita_creada: false,
    cita_cancelada: false,
    cita_autorizada: false,
  });

  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Cargar las notificaciones activas al abrir el modal
  useEffect(() => {
    if (open && clinicId) {
      loadNotifications();
    }
  }, [open, clinicId]);

  const loadNotifications = async () => {
    setLoading(true);
    setHasError(false);
    setErrorMessage("");
    try {
      const response = await staffAppointmentService.getActiveNotifications(
        clinicId
      );

      if (response.error) {
        setHasError(true);
        setErrorMessage(response.error);
      } else {
        // Convertir "true"/"false" strings a booleanos
        setNotificationSettings({
          recordatorio_cita: response.recordatorio_cita === "true",
          cita_rechazada: response.cita_rechazada === "true",
          cita_creada: response.cita_creada === "true",
          cita_cancelada: response.cita_cancelada === "true",
          cita_autorizada: response.cita_autorizada === "true",
        });
      }
    } catch (error) {
      console.error("Error al cargar notificaciones:", error);
      setHasError(true);
      setErrorMessage("Error al cargar las notificaciones");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (setting) => {
    // Actualizar el estado local primero
    const newValue = !notificationSettings[setting];
    setNotificationSettings((prev) => ({
      ...prev,
      [setting]: newValue,
    }));

    // Guardar inmediatamente en el backend
    try {
      // Crear objeto con el nuevo estado
      const dataToSend = {
        recordatorio_cita:
          setting === "recordatorio_cita"
            ? newValue
              ? "true"
              : "false"
            : notificationSettings.recordatorio_cita
            ? "true"
            : "false",
        cita_rechazada:
          setting === "cita_rechazada"
            ? newValue
              ? "true"
              : "false"
            : notificationSettings.cita_rechazada
            ? "true"
            : "false",
        cita_creada:
          setting === "cita_creada"
            ? newValue
              ? "true"
              : "false"
            : notificationSettings.cita_creada
            ? "true"
            : "false",
        cita_cancelada:
          setting === "cita_cancelada"
            ? newValue
              ? "true"
              : "false"
            : notificationSettings.cita_cancelada
            ? "true"
            : "false",
        cita_autorizada:
          setting === "cita_autorizada"
            ? newValue
              ? "true"
              : "false"
            : notificationSettings.cita_autorizada
            ? "true"
            : "false",
      };

      const response = await staffAppointmentService.updateNotifications(
        clinicId,
        dataToSend
      );

      if (response.response) {
        setSnackbar({
          open: true,
          message: response.mensaje || "Notificación actualizada correctamente",
          severity: "success",
        });
      } else {
        // Revertir el cambio si falla
        setNotificationSettings((prev) => ({
          ...prev,
          [setting]: !newValue,
        }));
        setSnackbar({
          open: true,
          message: "Error al actualizar la notificación",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error al guardar notificación:", error);
      // Revertir el cambio si hay error
      setNotificationSettings((prev) => ({
        ...prev,
        [setting]: !newValue,
      }));
      setSnackbar({
        open: true,
        message: "Error al guardar la notificación",
        severity: "error",
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        Ajuste de Notificaciones por WhatsApp
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {loading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        ) : hasError ? (
          <Box textAlign="center" py={4}>
            <Typography variant="h6" color="textSecondary">
              {errorMessage}
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Activa o desactiva el envío de notificaciones para cada tipo de
              evento. <br></br>
              <br></br>
              Ten en cuenta que al activar el envío de mensajes por WhatsApp,
              cada mensaje enviado tiene un costo, mientras más notificaciones
              mantengas activas, mayor será el costo cobrado por META debido al
              aumento en la cantidad de mensajes enviados.
            </Typography>

            <Box mt={2}>
              <div className={classes.settingRow}>
                <Box>
                  <Typography className={classes.settingLabel}>
                    Recordatorio de cita
                  </Typography>
                  <Typography className={classes.settingDescription}>
                    Envío de notificación al paciente como recordatorio antes de
                    su cita programada
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationSettings.recordatorio_cita}
                      onChange={() => handleToggle("recordatorio_cita")}
                      color="primary"
                    />
                  }
                  label=""
                />
              </div>
              <Divider />

              <div className={classes.settingRow}>
                <Box>
                  <Typography className={classes.settingLabel}>
                    Citas no autorizadas
                  </Typography>
                  <Typography className={classes.settingDescription}>
                    Envío de notificación al paciente cuando su cita no es
                    autorizada
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationSettings.cita_rechazada}
                      onChange={() => handleToggle("cita_rechazada")}
                      color="primary"
                    />
                  }
                  label=""
                />
              </div>
              <Divider />

              <div className={classes.settingRow}>
                <Box>
                  <Typography className={classes.settingLabel}>
                    Citas agendadas
                  </Typography>
                  <Typography className={classes.settingDescription}>
                    Envío de notificación al paciente cuando se crea
                    exitosamente una nueva cita
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationSettings.cita_creada}
                      onChange={() => handleToggle("cita_creada")}
                      color="primary"
                    />
                  }
                  label=""
                />
              </div>
              <Divider />

              <div className={classes.settingRow}>
                <Box>
                  <Typography className={classes.settingLabel}>
                    Citas canceladas
                  </Typography>
                  <Typography className={classes.settingDescription}>
                    Envío de notificación al paciente cuando se cancela una cita
                    previamente agendada
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationSettings.cita_cancelada}
                      onChange={() => handleToggle("cita_cancelada")}
                      color="primary"
                    />
                  }
                  label=""
                />
              </div>
              <Divider />

              <div className={classes.settingRow}>
                <Box>
                  <Typography className={classes.settingLabel}>
                    Citas autorizadas
                  </Typography>
                  <Typography className={classes.settingDescription}>
                    Envío de notificación al paciente cuando su cita es
                    autorizada exitosamente
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationSettings.cita_autorizada}
                      onChange={() => handleToggle("cita_autorizada")}
                      color="primary"
                    />
                  }
                  label=""
                />
              </div>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="default">
          Cerrar
        </Button>
      </DialogActions>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}

export default NotificationsSettingsModal;
